/**
 * @file routes/_app/settings/standards.tsx
 * @description 驗證標準庫管理頁面（Task 69）。
 *
 * 功能：
 * - 列表查詢（code / scheme / title / 生效日期）
 * - 新增/編輯標準（code / scheme / title / body / clauses JSON）
 * - 軟刪除
 *
 * 視覺：ListPageShell + FilterBar + StatusPill
 */

import { createFileRoute } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Search, Edit3, BookOpen, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, PageMetaItem, FilterBar, StatusPill } from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/standards')({ component: StandardsPage });

/**
 * 標準實體類型（對應後端 standards 表）。
 */
interface Standard {
  id: number;
  code: string;
  scheme: string;
  title: string;
  body: string | null;
  clauses: unknown;
  effectiveAt: string;
  retiredAt: string | null;
}

/** 後端分頁回應結構 */
interface StandardListResponse {
  items: Standard[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 驗證標準庫管理頁面 */
function StandardsPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStandard, setEditingStandard] = useState<Standard | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterScheme, setFilterScheme] = useState<string | undefined>();
  const [form] = Form.useForm<Record<string, unknown>>();

  /** 查詢標準列表 */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-standards', searchText, filterScheme],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      if (filterScheme) params.set('scheme', filterScheme);
      return apiClient.get<StandardListResponse>(`/api/settings/standards?${params.toString()}`);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const activeCount = items.filter((s) => !s.retiredAt).length;

  /** 開啟新增表單 */
  const handleAdd = () => {
    setEditingStandard(null);
    form.resetFields();
    setModalOpen(true);
  };

  /** 開啟編輯表單 */
  const handleEdit = (record: Standard) => {
    setEditingStandard(record);
    form.setFieldsValue({
      ...record,
      effectiveAt: record.effectiveAt ? dayjs(record.effectiveAt) : undefined,
      retiredAt: record.retiredAt ? dayjs(record.retiredAt) : undefined,
      clausesJson: record.clauses ? JSON.stringify(record.clauses, null, 2) : '',
    });
    setModalOpen(true);
  };

  /** 表格欄位 */
  const columns: ColumnsType<Standard> = [
    {
      title: '代碼',
      dataIndex: 'code',
      width: 140,
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.brand.primary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: '方案',
      dataIndex: 'scheme',
      width: 80,
      render: (v: string) => (
        <StatusPill tone="brand" size="sm" variant="outline">
          {v}
        </StatusPill>
      ),
    },
    {
      title: '標準名稱',
      dataIndex: 'title',
      render: (v: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
      ),
    },
    {
      title: '生效日期',
      dataIndex: 'effectiveAt',
      width: 130,
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {new Date(v).toLocaleDateString('zh-TW')}
        </span>
      ),
    },
    {
      title: '廢止日期',
      dataIndex: 'retiredAt',
      width: 130,
      render: (v: string | null) =>
        v ? (
          <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.state.danger }}>
            {new Date(v).toLocaleDateString('zh-TW')}
          </span>
        ) : (
          <StatusPill tone="success" size="sm" dot>
            仍有效
          </StatusPill>
        ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="編輯">
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={14} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="驗證標準管理"
        description="維護各驗證方案所適用的法規與技術標準，支援版本與廢止管理。"
        meta={
          <>
            <PageMetaItem icon={<BookOpen size={13} />} label="標準總數" value={`${total} 筆`} />
            <PageMetaItem label="仍有效" value={`${activeCount} 筆`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增標準
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋代碼 / 名稱"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
            filters={
              <Select
                placeholder="方案"
                value={filterScheme}
                onChange={setFilterScheme}
                allowClear
                style={{ width: 110 }}
                options={[
                  { value: 'TOP', label: 'TOP' },
                  { value: 'TAP', label: 'TAP' },
                  { value: 'ECO', label: 'ECO' },
                ]}
              />
            }
            extra={
              (searchText || filterScheme) && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => {
                    setSearchText('');
                    setFilterScheme(undefined);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<Standard>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 900 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 筆資料`,
          }}
        />
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editingStandard ? '編輯標準' : '新增標準'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <StandardForm
          item={editingStandard}
          onClose={() => {
            setModalOpen(false);
            form.resetFields();
            void qc.invalidateQueries({ queryKey: ['settings-standards'] });
          }}
          form={form}
        />
      </Modal>
    </>
  );
}

/**
 * 標準新增/編輯表單。
 * clauses 使用 JSON textarea，P0 接受任意 JSON；M1 改為結構化編輯器。
 */
function StandardForm({
  item,
  onClose,
  form,
}: {
  item: Standard | null;
  onClose: () => void;
  form: ReturnType<typeof Form.useForm<Record<string, unknown>>>[0];
}) {
  const qc = useQueryClient();

  /** 新增標準 Mutation */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/settings/standards', v),
    onSuccess: () => {
      message.success('標準已新增');
      void qc.invalidateQueries({ queryKey: ['settings-standards'] });
      onClose();
    },
  });

  /** 更新標準 Mutation */
  const update = useMutation({
    mutationFn: (v: unknown) => apiClient.patch(`/api/settings/standards/${item!.id}`, v),
    onSuccess: () => {
      message.success('標準已更新');
      void qc.invalidateQueries({ queryKey: ['settings-standards'] });
      onClose();
    },
  });

  /**
   * 表單提交前置處理：
   * - 將 DatePicker 值轉為 ISO 8601 字串
   * - 將 clauses JSON textarea 字串解析（容錯：解析失敗時原樣傳遞）
   */
  function handleFinish(values: Record<string, unknown>) {
    const payload: Record<string, unknown> = { ...values };

    // dayjs 對象轉為 ISO 字串
    if (values.effectiveAt && dayjs.isDayjs(values.effectiveAt)) {
      payload.effectiveAt = values.effectiveAt.toISOString();
    }
    if (values.retiredAt && dayjs.isDayjs(values.retiredAt)) {
      payload.retiredAt = values.retiredAt.toISOString();
    }

    // 嘗試解析 clauses JSON 字串
    if (typeof values.clausesJson === 'string' && values.clausesJson.trim()) {
      try {
        payload.clauses = JSON.parse(values.clausesJson);
      } catch {
        payload.clauses = values.clausesJson; // 解析失敗時原樣傳遞
      }
    }
    delete payload.clausesJson;

    if (item) {
      update.mutate(payload);
    } else {
      create.mutate(payload);
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={item ? {} : {}}
      onFinish={handleFinish}
      style={{ marginTop: 16 }}
    >
      {/* 標準代碼 + 適用方案 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="code" label="標準代碼" rules={[{ required: true, max: 100 }]}>
            <Input placeholder="如 TAP-2024" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="scheme" label="適用方案" rules={[{ required: true, max: 10 }]}>
            <Input placeholder="TOP / TAP / ECO" />
          </Form.Item>
        </Col>
      </Row>

      {/* 生效日期 + 廢止日期 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="effectiveAt" label="生效日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="retiredAt" label="廢止日期（留空 = 仍有效）">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      {/* 標準名稱（全寬） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="title" label="標準名稱" rules={[{ required: true, max: 300 }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* 標準正文（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="body" label="標準正文">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Col>
      </Row>

      {/* 條款結構 JSON（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="clausesJson" label="條款結構（JSON；P0 可留空，M1 改為結構化編輯器）">
            <Input.TextArea rows={4} placeholder='{"1": "第一條..."}' />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ marginBottom: 0 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '新增'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
